import { useEffect, useRef, useState } from "react";

import { useCatalog } from "../../catalog/CatalogContext";
import { CATEGORY_LABELS, Product, buildProductSlug } from "../../data/products";
import { getSiteUrl } from "../../utils/siteUrl";

type OrderModalProps = {
  product: Product | null;
  onClose: () => void;
};

type OrderForm = {
  phone: string;
  address: string;
  size: string;
  note: string;
};

const emptyForm: OrderForm = {
  phone: "",
  address: "",
  size: "",
  note: "",
};

export default function OrderModal({ product, onClose }: OrderModalProps) {
  const { store } = useCatalog();
  const [form, setForm] = useState<OrderForm>(emptyForm);
  const firstInputRef = useRef<HTMLInputElement | null>(null);
  const lastActiveRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!product) return;

    lastActiveRef.current = document.activeElement as HTMLElement | null;
    setForm(emptyForm);

    const focusTimer = window.setTimeout(() => {
      firstInputRef.current?.focus();
    }, 0);

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", onKeyDown);

    return () => {
      window.clearTimeout(focusTimer);
      window.removeEventListener("keydown", onKeyDown);
      lastActiveRef.current?.focus?.();
    };
  }, [product, onClose]);

  if (!product) return null;

  const requiresSize = product.category === "shoes";
  const isOutOfStock = (product.stock ?? 0) <= 0;
  const isDisabled =
    !form.phone ||
    !form.address ||
    (requiresSize && !form.size) ||
    isOutOfStock;

  const productUrl = `${getSiteUrl()}/product/${buildProductSlug(product)}`;

  const sendToWhatsApp = () => {
    const message = `
Bonjour
Je souhaite commander :

Produit : ${product.title}
Prix : ${product.price}
Categorie : ${CATEGORY_LABELS[product.category]}
${requiresSize ? `Pointure : ${form.size}\n` : ""}
Telephone : ${form.phone}
Adresse : ${form.address}
Note : ${form.note || "Aucune"}
${store.instructions ? `Instructions : ${store.instructions}\n` : ""}
Lien : ${productUrl}
    `;

    const url = `https://wa.me/${store.whatsappNumber}?text=${encodeURIComponent(
      message
    )}`;

    window.open(url, "_blank");
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-[rgba(20,10,5,0.64)] px-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="order-title"
      onClick={onClose}
    >
      <div
        className="surface-panel relative w-full max-w-lg rounded-[34px] p-6 md:p-8"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-5 top-5 flex h-11 w-11 items-center justify-center rounded-full border border-[rgba(62,35,24,0.1)] bg-white/70 text-xl text-[#2c1b15]"
          aria-label="Fermer"
        >
          ×
        </button>

        <p className="brand-eyebrow">Commander</p>
        <h3 id="order-title" className="brand-display mt-4 text-3xl font-bold text-[#21140f]">
          {product.title}
        </h3>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <span className="rounded-full bg-[#241712] px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-[#fff1e5]">
            {product.price}
          </span>
          <span className="rounded-full bg-white/70 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-[#7a6153]">
            {CATEGORY_LABELS[product.category]}
          </span>
        </div>

        <div className="mt-8 grid gap-4">
          <input
            ref={firstInputRef}
            id="order-phone"
            type="tel"
            placeholder="Numero de telephone"
            className="rounded-[20px] border border-[rgba(62,35,24,0.1)] bg-white/80 px-4 py-4 text-sm text-[#201410] outline-none placeholder:text-[#9f8576]"
            value={form.phone}
            onChange={(event) => setForm({ ...form, phone: event.target.value })}
          />

          <input
            id="order-address"
            type="text"
            placeholder="Adresse de livraison"
            className="rounded-[20px] border border-[rgba(62,35,24,0.1)] bg-white/80 px-4 py-4 text-sm text-[#201410] outline-none placeholder:text-[#9f8576]"
            value={form.address}
            onChange={(event) => setForm({ ...form, address: event.target.value })}
          />

          {requiresSize && (
            <input
              id="order-size"
              type="text"
              placeholder="Pointure"
              className="rounded-[20px] border border-[rgba(62,35,24,0.1)] bg-white/80 px-4 py-4 text-sm text-[#201410] outline-none placeholder:text-[#9f8576]"
              value={form.size}
              onChange={(event) => setForm({ ...form, size: event.target.value })}
            />
          )}

          <textarea
            id="order-note"
            placeholder="Note complementaire"
            rows={4}
            className="rounded-[20px] border border-[rgba(62,35,24,0.1)] bg-white/80 px-4 py-4 text-sm text-[#201410] outline-none placeholder:text-[#9f8576]"
            value={form.note}
            onChange={(event) => setForm({ ...form, note: event.target.value })}
          />

          <button
            type="button"
            onClick={sendToWhatsApp}
            disabled={isDisabled}
            className="cta-primary rounded-full px-6 py-4 text-xs font-semibold uppercase tracking-[0.3em] disabled:opacity-40"
          >
            Envoyer ma commande
          </button>

          <p className="text-center text-[11px] uppercase tracking-[0.2em] text-[#8b7263]">
            {isOutOfStock
              ? "Rupture de stock, contactez-nous pour la prochaine disponibilite"
              : `${store.name} vous repond rapidement pour confirmer votre commande`}
          </p>
        </div>
      </div>
    </div>
  );
}
